import BooleanColumnFilter from '@/components/ColumnFilters/BooleanColumnFilter';
import DateRangeColumnFilter from '@/components/ColumnFilters/DateRangeColumnFilter';
import NumberRangeColumnFilter from '@/components/ColumnFilters/NumberRangeColumnFilter';
import SelectColumnFilter from '@/components/ColumnFilters/SelectColumnFilter';
import TextColumnFilter from '@/components/ColumnFilters/TextColumnFilter';
import SusContact from '@/components/Sus/detail/SusContact';
import SusDeleteButton from '@/components/Sus/buttons/SusDeleteButton';
import SusEditButton from '@/components/Sus/buttons/SusEditButton';
import SusIngestionsButton from '@/components/Sus/buttons/SusIngestionsButton';
import SusViewButton from '@/components/Sus/buttons/SusViewButton';
import { useGetSusQuery } from '@/features/api/endpoints/suEndpoints';
import type { ColumnFilterProps, FlowFilter } from '@/models/flowFilter.type';
import {
  susListFields,
  susNumeroOrdreCodes,
  susSortColumns,
  type SUSRow,
  type SUSSortColumn,
} from '@/models/sus.type';
import useColumnFilters from '@/shared/hooks/useColumnFilters';
import { DATE_FORMAT } from '@/shared/utils/date-utils';
import { Box, Button, Group, Stack, Text } from '@mantine/core';
import { IconFilterOff } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import dayjs from 'dayjs';
import { DataTable, type DataTableColumn, type DataTableSortStatus } from 'mantine-datatable';
import { useCallback, useMemo, useState, type ReactNode } from 'react';

const RECORDS_PER_PAGE_OPTIONS = [25, 50, 100];
const EMPTY_VALUE = '—';
/** Colonnes interrogées par la recherche texte (`_icontains`, combinées en `_or`). */
const SEARCH_COLUMNS = ['nom', 'finess_geo'] as const satisfies readonly SUSSortColumn[];

const isSusSortColumn = (value: unknown): value is SUSSortColumn =>
  susSortColumns.includes(value as SUSSortColumn);

type Props = {
  /** Affiche le bouton d'édition (administrateurs). */
  canEdit: boolean;
  /** Recherche texte (déjà temporisée) sur le nom et le FINESS géographique. */
  search: string;
  /** Lignes cochées, gérées par la page pour afficher l'action de suppression près du titre. */
  selectedRecords: SUSRow[];
  onSelectedRecordsChange: (records: SUSRow[]) => void;
};

/** Le périmètre (tous les services pour un admin, ceux de ses établissements sinon) est imposé par le flow. */
const SusDataTable = ({ canEdit, search, selectedRecords, onSelectedRecordsChange }: Props) => {
  const { t } = useTranslate();
  const [page, setPage] = useState(1);
  const [previousSearch, setPreviousSearch] = useState(search);

  // Une nouvelle recherche ramène à la première page (la page vide déjà la sélection).
  if (search !== previousSearch) {
    setPreviousSearch(search);
    setPage(1);
  }
  const [recordsPerPage, setRecordsPerPage] = useState(RECORDS_PER_PAGE_OPTIONS[0]);
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<SUSRow>>({
    columnAccessor: 'finess_geo',
    direction: 'asc',
  });

  // La sélection ne survit pas à un changement de vue : pas de lignes sélectionnées invisibles.
  const resetPage = useCallback(() => {
    setPage(1);
    onSelectedRecordsChange([]);
  }, [onSelectedRecordsChange]);
  const { filters, setFilter, resetAll, isFiltering, hasFilters, flowFilter } = useColumnFilters({
    onChange: resetPage,
  });

  const { columnAccessor, direction } = sortStatus;

  // La recherche texte s'ajoute (en `AND`) aux filtres de colonnes.
  const filter = useMemo((): FlowFilter | undefined => {
    if (!search) return flowFilter;
    const searchFilter: FlowFilter = {
      _or: SEARCH_COLUMNS.map(column => ({ [column]: { _icontains: search } })),
    };
    return flowFilter ? { _and: [searchFilter, flowFilter] } : searchFilter;
  }, [search, flowFilter]);

  const { data, isError, isFetching } = useGetSusQuery({
    page,
    pageSize: recordsPerPage,
    fields: susListFields,
    orderBy: isSusSortColumn(columnAccessor) ? `${columnAccessor} ${direction}` : undefined,
    filter,
  });

  const columns = useMemo(() => {
    const title = (accessor: SUSSortColumn): string => t(`admin.sus.columns.${accessor}`);

    // Chaque helper renvoie les props `filter` / `filtering` d'une colonne du tableau.
    const filterProps = (
      accessor: SUSSortColumn,
      renderFilter: (props: ColumnFilterProps) => ReactNode,
    ) => ({
      filter: renderFilter({
        label: title(accessor),
        value: filters[accessor],
        onChange: value => setFilter(accessor, value),
      }),
      filtering: isFiltering(accessor),
    });

    const numberFilter = (accessor: SUSSortColumn) =>
      filterProps(accessor, props => <NumberRangeColumnFilter {...props} />);
    const textFilter = (accessor: SUSSortColumn) =>
      filterProps(accessor, props => <TextColumnFilter {...props} />);
    const dateFilter = (accessor: SUSSortColumn) =>
      filterProps(accessor, props => <DateRangeColumnFilter {...props} />);
    const booleanFilter = (accessor: SUSSortColumn) =>
      filterProps(accessor, props => <BooleanColumnFilter {...props} />);
    const numeroOrdreOptions = susNumeroOrdreCodes.map(code => ({
      value: String(code),
      label: t(`admin.sus.numero_ordre.${code}`),
    }));
    const numeroOrdreFilter = (accessor: SUSSortColumn) =>
      filterProps(accessor, props => (
        <SelectColumnFilter {...props} options={numeroOrdreOptions} />
      ));

    const dataColumns = [
      { accessor: 'id', title: title('id'), sortable: true, ...numberFilter('id') },
      {
        accessor: 'finess_geo',
        title: title('finess_geo'),
        sortable: true,
        ...textFilter('finess_geo'),
      },
      {
        accessor: 'numero_ordre',
        title: title('numero_ordre'),
        sortable: true,
        ...numeroOrdreFilter('numero_ordre'),
        render: ({ numero_ordre }) => (
          <Box
            style={{
              whiteSpace: 'nowrap',
            }}
          >
            {numero_ordre == null ? EMPTY_VALUE : t(`admin.sus.numero_ordre.${numero_ordre}`)}
          </Box>
        ),
      },
      { accessor: 'nom', title: title('nom'), sortable: true, ...textFilter('nom') },
      {
        accessor: 'format_rpu',
        title: title('format_rpu'),
        sortable: true,
        ...textFilter('format_rpu'),
        render: ({ format_rpu }) => format_rpu ?? EMPTY_VALUE,
      },
      {
        accessor: 'date_gel',
        title: title('date_gel'),
        textAlign: 'center',
        sortable: true,
        ...dateFilter('date_gel'),
        render: ({ date_gel }) => (date_gel ? dayjs(date_gel).format(DATE_FORMAT) : EMPTY_VALUE),
      },
      {
        accessor: 'contacts',
        title: t('admin.sus.columns.contacts'),
        render: ({ contacts }) =>
          contacts?.length ? (
            <Stack gap={2}>
              {[...contacts]
                .sort((a, b) =>
                  (a.libelle ?? '').localeCompare(b.libelle ?? '', 'fr', { sensitivity: 'base' }),
                )
                .map(contact => (
                  <SusContact key={contact.id ?? contact.email} contact={contact} />
                ))}
            </Stack>
          ) : (
            EMPTY_VALUE
          ),
      },
      {
        accessor: 'transmission_autorisee',
        title: title('transmission_autorisee'),
        sortable: true,
        textAlign: 'center',
        ...booleanFilter('transmission_autorisee'),
        render: ({ transmission_autorisee }) =>
          transmission_autorisee == null
            ? EMPTY_VALUE
            : transmission_autorisee
              ? t('admin.sus.transmission.oui')
              : t('admin.sus.transmission.non'),
      },
    ] satisfies DataTableColumn<SUSRow>[];

    const actionsColumn: DataTableColumn<SUSRow> = {
      accessor: 'actions',
      title: '',
      textAlign: 'center',
      pinned: 'right',
      render: ({ id }) => (
        <Group gap={4} wrap="nowrap" justify="center">
          <SusViewButton id={id} />
          {canEdit && <SusIngestionsButton id={id} />}
          {canEdit && <SusEditButton id={id} />}
          {canEdit && <SusDeleteButton id={id} />}
        </Group>
      ),
    };
    return [...dataColumns, actionsColumn] as DataTableColumn<SUSRow>[];
  }, [t, canEdit, filters, setFilter, isFiltering]);

  if (isError) {
    return (
      <Text c="red" role="alert">
        {t('admin.sus.error')}
      </Text>
    );
  }

  return (
    <Stack gap="sm">
      {hasFilters && (
        <Group justify="flex-end">
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconFilterOff size={16} stroke={1.5} />}
            onClick={resetAll}
          >
            {t('common.filters.resetAll')}
          </Button>
        </Group>
      )}
      <DataTable
        withTableBorder
        withColumnBorders
        minHeight={200}
        fetching={isFetching}
        records={data?.rows}
        idAccessor="id"
        columns={columns}
        selectedRecords={canEdit ? selectedRecords : undefined}
        onSelectedRecordsChange={canEdit ? onSelectedRecordsChange : undefined}
        noRecordsText={t('admin.sus.empty')}
        sortStatus={sortStatus}
        onSortStatusChange={status => {
          setSortStatus(status);
          resetPage();
        }}
        page={page}
        onPageChange={value => {
          setPage(value);
          onSelectedRecordsChange([]);
        }}
        totalRecords={data?.total}
        recordsPerPage={recordsPerPage}
        recordsPerPageOptions={RECORDS_PER_PAGE_OPTIONS}
        onRecordsPerPageChange={value => {
          setRecordsPerPage(value);
          resetPage();
        }}
        recordsPerPageLabel={t('admin.sus.recordsPerPage')}
        paginationWithEdges
        paginationText={({ from, to, totalRecords: total }) =>
          t('admin.sus.pagination', { from, to, total })
        }
      />
    </Stack>
  );
};

export default SusDataTable;
